import { useHttp, useHttpQuery } from '@mono/http-hook';
import { FC, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { FileVersion } from '../file-browser/types';
import { useCurrentOrganization } from '../organizations/hooks';
import {
  Badge,
  Box,
  Button,
  Card,
  Dialog,
  Flex,
  Select,
  Stack,
  Text,
  createListCollection,
} from '@chakra-ui/react';
import DiffEditor from './DiffEditor';
import { toaster, useColorMode } from '@mono/chakra';
import { HeaderRightPortal, HeadingPortal } from '@mono/layout';

type RevisionEnvironment = {
  id: string;
  name: string;
  key: string;
  isDefault: boolean;
};

type FileRevisionSummary = Omit<FileVersion, 'data'> & {
  environments?: RevisionEnvironment[];
};

const FileRevisionsPage: FC = () => {
  const colormode = useColorMode();
  const http = useHttp();
  const [params, setParams] = useSearchParams({
    path: '',
    from: '',
    to: '',
  });
  const filePath = params.get('path') ?? '';
  const fromRevisionParam = params.get('from') ?? '';
  const toRevisionParam = params.get('to') ?? '';
  const org = useCurrentOrganization();
  const { projectId } = useParams<{ projectId: string }>() as {
    projectId: string;
  };

  const [isPublishOpen, setIsPublishOpen] = useState(false);
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);

  const versionsQuery = useHttpQuery<{
    versions: FileRevisionSummary[];
  }>({
    queryKey: ['file-editor-versions', org?.id, projectId, filePath],
    config: {
      method: 'get',
      url:
        org && projectId
          ? `/api/v1/organizations/${org.id}/projects/${projectId}/file-tree/file/versions`
          : '',
      params: { path: filePath },
      withCredentials: true,
    },
    options: {
      enabled: Boolean(org?.id && projectId && filePath),
    },
  });

  const environmentsQuery = useHttpQuery<{
    environments: RevisionEnvironment[];
  }>({
    queryKey: ['project-environments', org?.id, projectId],
    config: {
      method: 'get',
      url:
        org && projectId
          ? `/api/v1/organizations/${org.id}/projects/${projectId}/environments`
          : '',
      withCredentials: true,
    },
    options: {
      enabled: Boolean(org?.id && projectId),
    },
  });

  const versions = useMemo(
    () => versionsQuery.data?.versions ?? [],
    [versionsQuery.data?.versions]
  );

  const resolvedToIndex = useMemo(() => {
    if (versions.length === 0) return -1;
    if (toRevisionParam) {
      const matchIndex = versions.findIndex(
        (revision) => revision.id === toRevisionParam
      );
      if (matchIndex >= 0) return matchIndex;
    }
    return 0;
  }, [toRevisionParam, versions]);

  const resolvedFromIndex = useMemo(() => {
    if (versions.length === 0) return -1;
    if (fromRevisionParam) {
      const matchIndex = versions.findIndex(
        (revision) => revision.id === fromRevisionParam
      );
      if (matchIndex >= 0) return matchIndex;
    }
    const fallback = resolvedToIndex + 1;
    return fallback < versions.length ? fallback : -1;
  }, [fromRevisionParam, resolvedToIndex, versions]);

  const currentRevision = resolvedToIndex >= 0 ? versions[resolvedToIndex] : null;
  const previousRevision = resolvedFromIndex >= 0 ? versions[resolvedFromIndex] : null;
  const currentEnvironments = currentRevision?.environments ?? [];
  const currentVersionId = currentRevision?.id ?? '';
  const previousVersionId = previousRevision?.id ?? '';
  const totalRevisions = versions.length;
  const formatTimestamp = (value?: string | null) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString();
  };
  const orderedRevisions = useMemo(
    () => versions.map((revision, index) => ({ revision, index })),
    [versions]
  );

  const environmentCollection = useMemo(
    () =>
      createListCollection({
        items:
          environmentsQuery.data?.environments.map((environment) => ({
            label: environment.name,
            value: environment.id,
          })) ?? [],
      }),
    [environmentsQuery.data?.environments]
  );

  const handleOpenPublish = () => {
    setSelectedEnvironmentId('');
    setIsPublishOpen(true);
  };

  const handleClosePublish = () => {
    if (isPublishing) return;
    setIsPublishOpen(false);
  };

  const handlePublish = async () => {
    if (
      !org ||
      !projectId ||
      !filePath ||
      !currentVersionId ||
      !selectedEnvironmentId
    ) {
      return;
    }

    setIsPublishing(true);
    try {
      await http.post(
        `/api/v1/organizations/${org.id}/projects/${projectId}/file-tree/file/versions/publish`,
        {
          path: filePath,
          versionId: currentVersionId,
          environmentId: selectedEnvironmentId,
        },
        { withCredentials: true }
      );

      await versionsQuery.refetch();
      setIsPublishOpen(false);
      toaster.create({
        type: 'success',
        title: 'Revision published',
        description:
          'The revision is now available in the selected environment.',
      });
    } catch (error) {
      toaster.create({
        type: 'error',
        title: 'Publish failed',
        description: 'Unable to publish this revision right now.',
      });
    } finally {
      setIsPublishing(false);
    }
  };

  const currentContentQuery = useHttpQuery<{ version: FileVersion }>({
    queryKey: [
      'file-version',
      org?.id,
      projectId,
      filePath,
      currentVersionId,
    ],
    config: {
      method: 'get',
      url:
        org && projectId
          ? `/api/v1/organizations/${org.id}/projects/${projectId}/file-tree/file/version`
          : '',
      params: { path: filePath, versionId: currentVersionId },
      withCredentials: true,
    },
    options: {
      enabled: Boolean(org?.id && projectId && filePath && currentVersionId),
    },
  });

  const previousContentQuery = useHttpQuery<{ version: FileVersion }>({
    queryKey: [
      'file-version',
      org?.id,
      projectId,
      filePath,
      previousVersionId,
    ],
    config: {
      method: 'get',
      url:
        org && projectId
          ? `/api/v1/organizations/${org.id}/projects/${projectId}/file-tree/file/version`
          : '',
      params: { path: filePath, versionId: previousVersionId },
      withCredentials: true,
    },
    options: {
      enabled: Boolean(org?.id && projectId && filePath && previousVersionId),
    },
  });

  const currentContent =
    currentContentQuery.data?.version?.id === currentVersionId
      ? currentContentQuery.data.version.data
      : '';
  const previousContent =
    previousContentQuery.data?.version?.id === previousVersionId
      ? previousContentQuery.data.version.data
      : '';

  return (
    <Box position={'absolute'} top={0} left={0} width={'100%'} height={'100%'}>
      <HeaderRightPortal>
        <Flex gap={3} align="center">
          <Box display={'flex'} alignItems={'center'} gap={1}>
            {currentEnvironments.length > 0 ? (
              currentEnvironments.map((environment) => (
                <Badge key={environment.id}>{environment.name}</Badge>
              ))
            ) : (
              <Badge variant="outline">Unpublished</Badge>
            )}
          </Box>
          <Button
            size="sm"
            variant="solid"
            borderRadius={'3xl'}
            onClick={handleOpenPublish}
            disabled={!currentVersionId}
          >
            Publish
          </Button>
        </Flex>
      </HeaderRightPortal>
      <Box
        position={'absolute'}
        height={'100%'}
        left={0}
        top={0}
        width={'100%'}
      >
        <HeadingPortal>
          {filePath || 'Select a file to view revisions'}
        </HeadingPortal>
        {versionsQuery.isLoading ? (
          <Box px={6} py={4}>
            <Text color="fg.muted">Loading revisions…</Text>
          </Box>
        ) : versionsQuery.error ? (
          <Box px={6} py={4}>
            <Text color="fg.error">Unable to load revisions.</Text>
          </Box>
        ) : totalRevisions === 0 ? (
          <Box px={6} py={4}>
            <Text color="fg.muted">No revisions to compare yet.</Text>
          </Box>
        ) : (
          <Flex
            gap={4}
            direction={{ base: 'column', lg: 'row' }}
            height={'100%'}
            p={3}
            boxSizing={'border-box'}
          >
            <Box w={{ base: 'full', lg: '320px' }} height={'100%'}>
              <Card.Root height={'100%'}>
                <Card.Header gap="1">
                  <Card.Title>Revision timeline</Card.Title>
                  <Card.Description>
                    Jump to any revision and publish it to an environment.
                  </Card.Description>
                </Card.Header>
                <Card.Body height={'100%'} overflow={'scroll'}>
                  <Stack gap="3" height={'100%'} overflow={'scroll'}>
                    {orderedRevisions.map((entry, index) => {
                      const revision = entry.revision;
                      const environments = revision?.environments ?? [];
                      const isActive = entry.index === resolvedToIndex;
                      return (
                        <Box
                          key={`${entry.index}-${revision?.id ?? index}`}
                          borderWidth="1px"
                          borderColor={
                            isActive ? 'border.emphasized' : 'border.subtle'
                          }
                          borderRadius="lg"
                          px="3"
                          py="2"
                          cursor="pointer"
                          bg={isActive ? 'bg.subtle' : 'transparent'}
                          onClick={() => {
                            const nextFrom = versions[entry.index + 1]?.id ?? '';
                            if (revision?.id) {
                              setParams(
                                {
                                  path: filePath,
                                  to: revision.id,
                                  from: nextFrom,
                                },
                                { replace: true }
                              );
                            }
                          }}
                        >
                          <Stack gap="2">
                            <Flex justify="space-between" align="center">
                              <Text fontWeight="semibold">
                                Revision {totalRevisions - index}
                              </Text>
                              {revision?.version ? (
                                <Badge variant="outline">
                                  v{revision.version}
                                </Badge>
                              ) : null}
                            </Flex>
                            <Text fontSize="xs" color="fg.muted">
                              Updated {formatTimestamp(revision?.updatedAt)}
                            </Text>
                            <Flex wrap="wrap" gap="1">
                              {environments.length > 0 ? (
                                environments.map((environment) => (
                                  <Badge
                                    key={environment.id}
                                    variant={
                                      environment.isDefault ? 'solid' : 'subtle'
                                    }
                                    colorPalette={
                                      environment.isDefault ? 'green' : 'gray'
                                    }
                                  >
                                    {environment.name}
                                  </Badge>
                                ))
                              ) : (
                                <Badge variant="outline">Unpublished</Badge>
                              )}
                            </Flex>
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                </Card.Body>
              </Card.Root>
            </Box>
            <Box flex="1" height={'100%'}>
              <Card.Root height={'100%'}>
                <Card.Body>
                  {currentContentQuery.isLoading ? (
                    <Text color="fg.muted">Loading revision content...</Text>
                  ) : currentContentQuery.error ? (
                    <Text color="fg.error">Unable to load revision content.</Text>
                  ) : null}
                  <DiffEditor
                    key={`${currentVersionId}/${previousVersionId}/${currentContentQuery.data?.version.updatedAt ?? ''}/${previousContentQuery.data?.version.updatedAt ?? ''}`}
                    colormode={colormode.colorMode}
                    previousValue={previousContent}
                    currentValue={currentContent}
                  />
                </Card.Body>
              </Card.Root>
            </Box>
          </Flex>
        )}
      </Box>
      <Dialog.Root
        open={isPublishOpen}
        onOpenChange={(details) => {
          if (!details.open) handleClosePublish();
        }}
      >
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.CloseTrigger />
            <Dialog.Header flexDirection="column" gap="1">
              <Dialog.Title>Publish revision</Dialog.Title>
              <Dialog.Description>
                Choose which environment should receive this revision.
              </Dialog.Description>
            </Dialog.Header>
            <Dialog.Body>
              <Box display="flex" flexDirection="column" gap="3">
                {environmentsQuery.isLoading ? (
                  <Text>Loading environments...</Text>
                ) : environmentsQuery.error ? (
                  <Text color="fg.error">Unable to load environments.</Text>
                ) : (
                  <Select.Root
                    collection={environmentCollection}
                    value={selectedEnvironmentId ? [selectedEnvironmentId] : []}
                    onValueChange={(details) => {
                      const [value] = details.value as string[];
                      setSelectedEnvironmentId(value ?? '');
                    }}
                    disabled={
                      isPublishing || environmentCollection.items.length === 0
                    }
                  >
                    <Select.HiddenSelect />
                    <Select.Control>
                      <Select.Trigger>
                        <Select.ValueText placeholder="Select environment" />
                        <Select.Indicator />
                      </Select.Trigger>
                    </Select.Control>
                    <Select.Positioner>
                      <Select.Content>
                        {environmentCollection.items.map((environment) => (
                          <Select.Item
                            item={environment}
                            key={environment.value}
                          >
                            {environment.label}
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                )}
              </Box>
            </Dialog.Body>
            <Dialog.Footer gap="3" justifyContent="flex-end">
              <Button
                variant="ghost"
                onClick={handleClosePublish}
                disabled={isPublishing}
              >
                Cancel
              </Button>
              <Button
                colorPalette="primary"
                onClick={handlePublish}
                loading={isPublishing}
                disabled={!selectedEnvironmentId || isPublishing}
              >
                Publish
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Root>
    </Box>
  );
};

export default FileRevisionsPage;
